import React, { useEffect, useState, useContext } from "react";
import { useHistory, useParams, useLocation } from "react-router";
import { useQuery, useMutation } from "@apollo/client";

import { getUserImage } from "../../../middleware/common-functions";
import { UserItem, UserSettings } from "../../../models/user";
import settingsNav from "./settings.json";
import SideNav from "./sidenav";
import Profile from "./profile";
import Email from "./emails";
import Appearance from "./appearance";
import Passwords from "./passwords";
import Student from "./students";
import GradesQuizzes from "./gradesquizzes";
import Applications from "./applications";
import LMSClasses from "./lms_classes";
import { LoaderPuff } from "../../../middleware/loaders";
import { SETPREFF } from "../profile/queries";
import { GET_USER } from "./queries";
import { DataContext } from "../../../context/data-context";
import {
  getAuthCookie,
  setAuthCookie,
} from "../../../middleware/cookie-manager";
import { Themes } from "../../../models/pageSettings";

const SettingsPage: React.FC = () => {
  const dataContext = useContext(DataContext);
  const history = useHistory();
  const location = useLocation();
  const { section = "profile", submenu } = useParams<{
    section: string;
    submenu: undefined | string;
  }>();
  const user = getAuthCookie(true);
  const [settings, setSettings] = useState<UserSettings>({
    angelos_show: false,
    time_zone: "America/New_York",
    hide_achievements: false,
    block_password_change: false,
    block_lang_selection: false,
    hide_quizzes: false,
    allow_retakes: false,
    no_password_login: false,
    theme: "light" as Themes,
  });
  const [activeSection, setActiveSection] = useState(section);
  const [loading, setLoading] = useState(false);
  const [setPreff, { loading: settingLoading }] = useMutation(SETPREFF);

  const { data: user_data, loading: userLoading } = useQuery<{
    user: UserItem;
    getSettings: UserSettings;
  }>(GET_USER, {
    fetchPolicy: "no-cache",
    skip: !user?.is_teacher,
  });

  const profile_pic = user?.profile_pic
    ? getUserImage(user.profile_pic)
    : "https://cdn.flangoo.com/assets/global/thumb1.png";

  useEffect(() => {
    if (user_data) {
      const updatedSettings = { ...user_data.getSettings };
      if (JSON.stringify(settings) !== JSON.stringify(updatedSettings)) {
        const updatedUser = { ...user, ...user_data.user };
        setAuthCookie(updatedUser);
        setSettings(updatedSettings);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_data]);

  useEffect(() => {
    const path = location.pathname;
    const sections = path.split("/");
    const section = sections[sections.length - 1];
    setActiveSection(section);
  }, [location]);

  useEffect(() => {
    if (settings) {
      console.log("settings", settings);
      dataContext.setSettings(settings);
    }
    // eslint-disable-next-line
  }, [settings]);

  const handleActiveSection = (section: string) => {
    let url = section.toLocaleLowerCase();
    ///update url with section name using history
    history.push(`/settings/${url}`);
  };

  const handleUpdateSetting = (setting: keyof UserSettings, value: any) => {
    const previousSettings = { ...settings };

    setSettings((prev) => ({ ...prev, [setting]: value }));

    setPreff({
      variables: {
        pref_value: value.toString(),
        pref_key: setting,
        is_global: true,
      },
      onError: () => {
        setSettings(previousSettings); // revert if it fails
      },
    });
  };

  type ComponentsMap = {
    [key: string]: JSX.Element | { [key: string]: JSX.Element };
  };

  const componentsMap: ComponentsMap = {
    profile: {
      default: (
        <Profile
          userData={user}
          profile_pic={profile_pic}
          setLoading={setLoading}
        />
      ),
    },
    emails: { default: <Email userData={user} /> },
    students: {
      default: (
        <Student settings={settings} setSettings={handleUpdateSetting} />
      ),
    },
    gradesquizzes: {
      default: (
        <GradesQuizzes
          settings={settings}
          setSettings={handleUpdateSetting}
          loading={settingLoading}
        />
      ),
    },
    security: { default: <Passwords userData={user} /> },
    appearance: { default: <Appearance setSettings={handleUpdateSetting} /> },
    applications: {
      classes: <LMSClasses />,
      default: <Applications />,
    },
    default: (
      <Profile
        userData={user}
        profile_pic={profile_pic}
        setLoading={setLoading}
      />
    ),
  };

  const renderSection = () => {
    const entry = componentsMap[section] as any;

    if (!entry) {
      return componentsMap.default;
    }

    if (typeof entry === "object" && !React.isValidElement(entry)) {
      if (submenu && entry[submenu]) {
        return entry[submenu];
      }
      return entry.default || componentsMap.default;
    }

    return entry;
  };

  if (!user || !settings) {
    return <div>Loading...</div>;
  }

  return (
    <main className="settings_page  first-container last-container">
      {loading || userLoading ? <LoaderPuff fixed /> : null}
      <div className="container-fluid">
        <h1>Settings</h1>
        <div className="row">
          <div className="col-md-3 mb-4 mb-md-0">
            <div className="row">
              <div className="col col-md-12 order-md-2">
                <SideNav
                  active={activeSection}
                  settings={settingsNav}
                  handleActiveSection={handleActiveSection}
                />
              </div>
              <div className="col-7 col-md-12 order-md-1 xs_hidden">
                <UserBlock user={user} profile_pic={profile_pic} />
              </div>
            </div>
          </div>
          <div className="col">
            {userLoading ? <LoaderPuff fixed /> : renderSection()}
          </div>
        </div>
      </div>
    </main>
  );
};

const UserBlock: React.FC<{ user: UserItem; profile_pic: string }> = ({
  user,
  profile_pic,
}) => {
  return (
    <div className="user_data">
      <img src={profile_pic} alt="User Profile" className="img-fluid" />
      <div>
        <h5>{user.name}</h5>
        <p>Your teacher account</p>
      </div>
    </div>
  );
};

export default SettingsPage;
